import type { ChangelogEntry } from './changelog-types';
import { changelogLegacy } from './changelog-legacy';

export type { ChangelogEntry, ChangelogSection, ChangelogItem, SectionType } from './changelog-types';

/** Conteúdo baseado na wiki oficial: https://wiki.xat.com/Changelog (espelho: xat.wiki/changelog). */
const changelogCore: ChangelogEntry[] = [
  {
    version: '1.68.1',
    date: '5 de novembro de 2025',
    latest: true,
    sections: [
      {
        type: 'fix',
        label: 'Correções de Bugs',
        count: 18,
        items: [
          { title: 'O autocompletar de smilies às vezes sugeria entradas incorretas, como números em vez de smilies de verdade.' },
          { title: 'YouTube Shorts não apareciam como vídeos incorporados e eram processados pelo LinkValidator.' },
          { title: 'Múltiplos problemas com o power Random foram corrigidos.' },
          { title: 'Múltiplos problemas com o power Sline foram corrigidos.' },
          { title: 'Desativar o power Zoom causava imagens borradas.' },
          { title: 'No seletor do 8ball, o smiley Count não era clicável e o Heartfx aparecia duplicado.' },
          { title: 'Ao usar o power Face, algumas combinações se comportavam como links.' },
          { title: 'Usar a palavra "hat" em uma mensagem de scroll a tornava clicável, redirecionando para a wiki do xat.' },
          { title: 'Às vezes, a lista de reações estava vazia.' },
          { title: 'Ao usar um chat incorporado em diferentes alturas, a lista de GIFs favoritos não se ajustava dinamicamente ao tamanho do embed.' },
          { title: 'A palavra "constructor" estava vinculada em todos os chats, mas redirecionava para uma página de erro.' },
          { title: 'O power Reverse se transformava em um efeito Jinx; digitar (reverse) o alterava para (jumblejinx#wreverse).' },
          { title: 'Usar o comando "unjinx all" exibia a mensagem para todos os usuários no PM, em vez de apenas para o usuário desjinxado.' },
          { title: 'O contador de usuários mostrava 0 na Quickbar mesmo com usuários ativos quando o pool principal e o de staff tinham o mesmo nome.' },
          { title: 'No app Grid, avatares GIF podiam falhar ao carregar quando um erro 403 ou 404 era retornado para a imagem.' },
          { title: '"Copiar mensagem" e "Copiar smiley" não funcionavam em chats incorporados.' },
          { title: 'Múltiplos problemas com o Translator foram corrigidos.' },
          {
            title: 'App Doodle',
            details: [
              'Desenhar uma linha sobre a lixeira apagava toda a imagem.',
              'Usuários não registrados não conseguiam desenhar.',
            ],
          },
        ],
      },
    ],
  },
  {
    version: '1.68',
    date: '11 de abril de 2025',
    sections: [
      {
        type: 'new',
        label: 'Novidades no Chat',
        count: 5,
        items: [
          {
            title: 'Edição rápida',
            details: [
              'As mensagens agora são editadas diretamente na própria mensagem.',
              'Pressionar a seta para cima ↑ abre rapidamente o editor da sua última mensagem.',
            ],
          },
          {
            title: 'Doodlerace2',
            details: [
              'O Doodlerace2 aprimora o app Doodle recém-lançado, com as mesmas ferramentas de desenho e um design atualizado para uma experiência de jogo melhorada.',
            ],
          },
          {
            title: 'Menu de apps 2',
            details: [
              'Design completamente novo com ícones de apps renovados.',
              'Você pode adicionar 4 apps favoritos, sempre exibidos no topo. Clique com o botão direito em um app para adicioná-lo ou removê-lo dos favoritos.',
              'Um ícone de ajuda e do menu de apps agora aparece à esquerda quando um app é aberto, facilitando o acesso à wiki e a navegação.',
            ],
          },
          {
            title: 'Jogo Sinuca',
            details: [
              'O jogo de sinuca voltou com uma jogabilidade melhorada! Jogue contra um bot ou desafie um amigo no chat privado.',
              'Um indicador de força agora aparece ao puxar o taco, e você pode clicar com o botão direito para cancelar.',
            ],
          },
          {
            title: 'Jogo de Xadrez',
            details: [
              'O xadrez agora está disponível com uma jogabilidade melhorada! Jogue contra um bot ou dispute uma partida com um amigo no chat privado.',
            ],
          },
        ],
      },
      {
        type: 'powers',
        label: 'Novidades em Powers',
        count: 53,
        items: [
          {
            title: 'Hugs',
            details: [
              'Abraços agora estão disponíveis.',
              'Usuários podem desativar os Hugs nas configurações.',
            ],
          },
          {
            title: 'Flix',
            details: [
              'Gerentes de grupo podem novamente definir animações Flix para seus chats.',
              'Usuários podem desativar animações Flix nas configurações.',
              'As animações Flix agora exigem apenas um power atribuído para funcionar. Por exemplo, um único power Feast desbloqueará todas as 4 animações.',
            ],
          },
          {
            title: 'Jinx',
            details: ['Os recursos do Jinx estão agora disponíveis.'],
          },
        ],
      },
      {
        type: 'improvement',
        label: 'Melhorias e Mudanças',
        count: 11,
        items: [
          { title: 'Pink e Blueman não são mais limitados ao rank de visitante/membro e agora funcionam como outras peças, aparecendo nas mensagens.' },
          { title: 'Smilies agora são compatíveis com efeitos de avatar e molduras.' },
          { title: 'Ao fechar um PC usando o atalho Esc ou clicando no ícone de fechar, agora ele muda para a aba à esquerda em vez de sempre voltar para a principal.' },
          { title: 'Atalhos de chat como %trade enviados por visitantes não serão mais visíveis para outros usuários, apenas para eles mesmos.' },
          {
            title: 'Autocompletar de smilies melhorado',
            details: [
              'Agora você pode pesquisar por palavras inteiras. Por exemplo, digitar "legal" irá sugerir "legal" mas também "reLEGAL" e correspondências semelhantes.',
              'Letras maiúsculas são suportadas.',
              'Usuários não registrados agora podem usar o autocompletar.',
            ],
          },
          { title: 'Agora você pode adicionar um efeito de smiley diretamente em Configurações > Powers > Efeito de smiley, sem precisar usar o comando /o.' },
          { title: 'Uma nova configuração foi adicionada para desativar os efeitos Ultra smiley.' },
          { title: 'O Count agora suporta um parâmetro UTC opcional para melhor sincronização. Exemplo: (count#h#utc/2/17/25). Uma nova caixa de seleção UTC também foi adicionada ao app Count.' },
          { title: 'O app Nuclear agora possui um modo escuro que pode ser ativado e desativado.' },
        ],
      },
      {
        type: 'fix',
        label: 'Correções de Bugs',
        count: 36,
        items: [
          { title: 'Era possível visualizar e enviar presentes para usuários que te bloquearam.' },
          { title: 'Você via beijos de usuários que havia bloqueado.' },
          { title: 'Ao abrir um vídeo e depois fechar o app para abri-lo novamente, os vídeos não carregavam mais.' },
          { title: 'Ao usar o Shuffle, o avatar ficava desalinhado às vezes.' },
          { title: 'No app de grade, múltiplos problemas que travavam o app foram corrigidos.' },
          { title: 'Uma determinada sintaxe de mensagem se comportava como um sticker.' },
          { title: 'Avatares de smiley no perfil estavam muito grandes.' },
          { title: 'No Firefox, smilies no 8ball não eram visíveis para usuários não registrados.' },
          { title: 'As abas mudavam ao usar as teclas de seta esquerda e direita enquanto o menu da Quickbar estava aberto.' },
          { title: 'O Supercycle não funcionava com um código hex totalmente preto.' },
          {
            title: 'Doodle2',
            details: [
              'Ao usar uma forma triangular e clicar no quadro de desenho, ela aparecia invertida.',
              'Inverter formas fazia com que se comportassem de forma diferente; agora são mantidas unidimensionais.',
              'Às vezes, desenhar em PC vazava para outros doodles privados.',
              'As coordenadas de linha às vezes apareciam incorretamente ao alternar entre abas de PC.',
            ],
          },
          { title: 'Usuários não registrados podiam enviar stickers sem possuir o power.' },
          {
            title: 'GIFs',
            details: [
              'A busca na Quickbar mostrava a última pesquisa após fechar e reabrir o menu.',
              'O menu de seleção não se ajustava corretamente quando o tamanho da tela era alterado.',
            ],
          },
          { title: 'Múltiplos problemas no app Count foram corrigidos.' },
          { title: 'Ao colar texto, o cursor sempre pulava para o primeiro caractere da caixa de texto.' },
          { title: 'Não era possível editar mensagens GIF.' },
          { title: 'A indicação "Editado" não era exibida em mensagens de sticker.' },
          { title: 'Às vezes, avatares como GIFs 2D ou strips apareciam borrados ou com baixa qualidade.' },
          { title: 'Toda vez que um usuário fazia login, o código de smiley padrão poderia ser redefinido para um Supercycle, mesmo tendo sido limpo ou alterado.' },
          { title: 'Usar um determinado código de chapéu tornava a peça invisível.' },
          { title: 'O ícone do app Pawns na lista de visitantes não era clicável.' },
          { title: 'Smilies gratuitos pertencentes a powers não eram listados para usuários no 8ball e na caixa de autocompletar.' },
          { title: 'Alguns smilies estavam faltando na lista de reações.' },
          { title: 'O menu de reações às mensagens podia estar vazio às vezes.' },
          { title: 'Salvar configurações de avatar falhava ocasionalmente.' },
          { title: 'Nas configurações, clicar em Salvar várias vezes durante o salvamento causava um travamento.' },
          { title: 'Na versão mais recente do Safari, a caixa de mensagem do chat aparecia desalinhada.' },
          { title: 'O power Sline não tratava corretamente espaços no final, nomes de smiley em maiúsculas ou códigos de cor hex.' },
          { title: 'Se a configuração de linha de smiley vazia estivesse ativada, os smilies apareciam ao passar o mouse, mas não desapareciam após sair.' },
          {
            title: 'App Mobile',
            details: [
              'No iOS Safari, dar rank a um usuário pela primeira vez causava um piscar branco.',
              'Digitar colchetes acionava a caixa de autocompletar, fazendo o teclado fechar. Agora permanece aberto até que o usuário interaja com ele.',
            ],
          },
          { title: 'Mensagens de confirmação, como as de redefinição de senha, exibiam a cor incorreta.' },
        ],
      },
    ],
  },
  {
    version: '1.67',
    date: '10 de novembro de 2024',
    sections: [
      {
        type: 'new',
        label: 'Novidades no Chat',
        count: 3,
        items: [
          {
            title: 'App Doodle v2',
            details: [
              'Design renovado, novos utilitários e usabilidade aprimorada.',
              'Ferramentas: retângulo, círculo, triângulo, linha, transparência, texto, borracha, balde de tinta, lápis e tamanho do pincel.',
              'Seletor de cores — é possível definir cores diferentes para o clique esquerdo e direito do mouse.',
              'Exportar o doodle como imagem.',
              'Importar imagem (requer o power Dood).',
              'Botão de limpar tela.',
            ],
          },
          {
            title: 'PCBack aprimorado',
            details: [
              'Defina fundos com cores sólidas usando códigos hex. Uso: avatar#codigohex.',
              'Suporte a imagens animadas (.gif). Requer 2 powers PCBack.',
            ],
          },
          {
            title: 'App de Troca',
            details: [
              'Powers de grupo agora podem ser desatribuídos diretamente no app de troca, quando necessário.',
              'Como funciona: após adicionar os powers e clicar em "Aceitar", pode aparecer uma janela pedindo para desatribuir ou remover os powers antes de concluir a troca.',
            ],
          },
        ],
      },
      {
        type: 'powers',
        label: 'Powers Adicionados',
        count: 4,
        items: [
          { title: 'Darts — novo gamerace app.' },
          { title: 'Spacewar — novo gamerace app.' },
          { title: 'Switch — novo gamerace app.' },
          { title: 'Hearts — novo gamerace app.' },
        ],
      },
      {
        type: 'improvement',
        label: 'Melhorias e Mudanças',
        count: 6,
        items: [
          { title: 'Experiência melhorada no Translator! O Translator não vai mais exigir "Por favor, faça login" o tempo todo.' },
          { title: 'Reações e edição de mensagens agora estão disponíveis para usuários registrados — antes era exclusivo para assinantes.' },
          { title: 'As reações agora incluem uma seleção muito maior de smilies (+261), além de uma opção de busca.' },
          { title: 'O ícone do app de Count, Nuclear, Stick e Supercycle agora aparece ao lado do nome quando aberto.' },
          { title: 'Uma nova configuração permite desativar animações de fundo (PCBack, Pstyle).' },
          { title: 'Ao adquirir um grupo, a senha não precisa mais ser redefinida para ter acesso. Agora você recebe uma senha inicial por e-mail.' },
        ],
      },
      {
        type: 'fix',
        label: 'Correções de Bugs',
        count: 13,
        items: [
          { title: 'Smilies com Invert ou Big podiam causar grandes espaços nas mensagens às vezes.' },
          { title: 'Múltiplos problemas com Reações foram corrigidos.' },
          { title: 'A peça de usuários offline com códigos de chapéu às vezes aparecia de forma diferente.' },
          { title: 'As opções de tempo de ban não apareciam em chats privados.' },
          { title: 'Não era possível editar ou excluir uma Categoria ou Palavra-chave após salvar as configurações.' },
          { title: 'Smilies da super coleção não apareciam no autocompletar.' },
          { title: 'Com o power Hush, os usuários permaneciam silenciados até reconectar.' },
          { title: 'Múltiplos problemas com os apps Grid e Count foram corrigidos.' },
          { title: 'Múltiplos problemas com o Statusfx foram corrigidos.' },
          { title: 'Smilies não apareciam na pré-visualização do nome durante a edição quando um código longo era utilizado.' },
          { title: 'Letras de fontes especiais às vezes desapareciam.' },
          { title: 'No app mobile, enviar GIFs grandes não exibia corretamente.' },
          { title: 'Incorporar um chat no xatspace não estava funcionando.' },
        ],
      },
    ],
  },
  // —— Versões adicionais (wiki.xat.com/Changelog) ——
  {
    version: '1.66',
    date: '3 de agosto de 2024',
    sections: [
      {
        type: 'new',
        label: 'Novidades no Chat',
        count: 5,
        items: [
          {
            title: 'App Grid',
            details: ['Lista usuários online com avatares; ao clicar, abre o xatspace.'],
          },
          {
            title: 'App Stick',
            details: ['Ferramenta avançada para montar códigos Stick. (No chat, sticks exigem o power Stick.)'],
          },
          {
            title: 'App Supercycle',
            details: ['Crie supercycles. (No chat, exige power Supercycle.)'],
          },
          {
            title: 'App Nuclear',
            details: ['Gere códigos nuclear. (No chat, exige power Nuclear.)'],
          },
          {
            title: 'App Count',
            details: ['Monte contagens regressivas. (No chat, exige power Count.)'],
          },
        ],
      },
      {
        type: 'powers',
        label: 'Powers / Apps de jogo',
        count: 3,
        items: [
          { title: 'Zwhack — adicionado gamerace app.' },
          { title: 'Matchrace — adicionado gamerace app.' },
          { title: 'Doodlerace — gamerace app e app Doodle adicionados.' },
        ],
      },
      {
        type: 'improvement',
        label: 'Melhorias e Mudanças',
        count: 1,
        items: [
          {
            title: 'App de troca',
            details: ['Opção para adicionar "1 unidade" de cada power de uma só vez.'],
          },
        ],
      },
      {
        type: 'fix',
        label: 'Correções de Bugs',
        count: 12,
        items: [
          { title: 'Iconcolor podia sobrepor o ícone de gag em certas situações.' },
          {
            title: 'GIFs',
            details: ['Seleção às vezes em uma única coluna.', 'Alguns GIFs enviados afetavam o scroll.'],
          },
          { title: 'Função Bloquear aparecia no diálogo de pré-visualização Pstyle quando não deveria.' },
          { title: 'Usuários gagados conseguiam enviar beijos.' },
          { title: 'Clicar no smiley (gifts) podia travar o chat.' },
          { title: 'Menu de contexto da mensagem não fechava ao mudar de aba de PC até clicar fora.' },
          { title: 'No mobile com Shuffle, a lista de visitantes mostrava strip em vez de frame aleatório do avatar.' },
          { title: 'Autocompletar de smilies exibia posição incorreta ao enviar PM.' },
          { title: 'Abrir parênteses e um espaço disparava o autocompletar de smilies indevidamente.' },
          { title: 'Ao escolher um smiley no autocompletar, o campo de mensagem perdia o foco.' },
          { title: 'Banners às vezes não apareciam com idioma português (web).' },
        ],
      },
    ],
  },
  {
    version: '1.65',
    date: '14 de maio de 2024',
    sections: [
      {
        type: 'new',
        label: 'Novidades no Chat',
        count: 1,
        items: [
          {
            title: 'Autocompletar de smilies',
            details: [
              'Pode ser desativado nas configurações do chat.',
              'Mostra smilies gratuitos e de powers que você possui.',
              'Ao selecionar, o nome do smiley autocompleta.',
              'Comece a digitar e aparecem sugestões para escolha.',
            ],
          },
        ],
      },
      {
        type: 'powers',
        label: 'Powers Adicionados',
        count: 3,
        items: [
          { title: 'Slotban (gameban).' },
          { title: 'Mazeban (gameban).' },
          { title: 'Matchban (gameban).' },
        ],
      },
      {
        type: 'improvement',
        label: 'Melhorias e Mudanças',
        count: 1,
        items: [
          {
            title: 'App de troca',
            details: [
              'Mensagem de sucesso deixou de aparecer em vermelho.',
              'Fechar o aviso com X cancela a operação em vez de ficar no estado de confirmação.',
              'Com mais de 10 unidades de um power, CTRL/CMD+clique define a quantidade a adicionar.',
              'Botão +all virou "+", com opções para coleções ou duplicados de uma vez.',
            ],
          },
        ],
      },
      {
        type: 'fix',
        label: 'Correções de Bugs',
        count: 10,
        items: [
          { title: 'Copiar/colar código (sticker, smiley, stick) adicionava espaço à frente e quebrava o código; corrigido.' },
          { title: 'Ativar ou desativar Stealth pela Quickbar e abrir Group Powers congelava a tela; corrigido.' },
          { title: 'Após login, alguns códigos de nome/smiley eram removidos ou alterados; corrigido.' },
          { title: 'Mensagens com aspas duplas ou links não eram traduzidas; corrigido.' },
          { title: 'Usuário banido podia abrir PM vazio; corrigido.' },
          { title: 'Códigos de smiley em URLs viravam smilies e quebravam o link; corrigido.' },
          { title: 'Certo caractere no avatar removia espaço na pré-visualização da mensagem; corrigido.' },
          { title: 'Diálogo de apagar presente tinha botão fechar inconsistente com outros; ajustado.' },
          { title: 'Supercycle linear não funcionava; corrigido.' },
          { title: 'Alguns avatares no menu da conta não carregavam com caractere especial no nome (web).' },
        ],
      },
    ],
  },
  {
    version: '1.64',
    date: '17 de março de 2024',
    sections: [
      {
        type: 'powers',
        label: 'Powers e recursos',
        count: 5,
        items: [
          {
            title: 'Vote',
            details: [
              'Visitantes não registrados não podem votar.',
              'Contagem e resultados também na Quickbar.',
              'Ao definir, notificação no chat ao conectar.',
              'Votar pela Quickbar; configurar em group powers.',
              'Vote recebeu um novo boost.',
            ],
          },
          {
            title: 'Count',
            details: [
              'Unidades: f (fixo), d (dias), h (horas), m (minutos), s (segundos); pode colorir números.',
              'Formato: (count#timestamp#corFundo#corNumeros).',
            ],
          },
          {
            title: 'Stick',
            details: ['Animações Stick passam a ser exibidas.'],
          },
          {
            title: 'Shuffle',
            details: ['Avatares podem ser embaralhados; novo conversor em xat.com/tools.'],
          },
          {
            title: 'Ttth',
            details: ['“Talk to the hand” de volta (ver wiki Ttth).'],
          },
        ],
      },
      {
        type: 'improvement',
        label: 'Melhorias e Mudanças',
        count: 2,
        items: [
          { title: 'Ao clicar em power de grupo, vê-se smilies e botão atribuir/desatribuir (antes só em powers não de grupo).' },
          { title: 'Translator: idioma guardado por chat (ex.: espanhol em /xat e francês em /trade).' },
        ],
      },
      {
        type: 'fix',
        label: 'Correções de Bugs',
        count: 9,
        items: [
          { title: 'Quickbar às vezes precisava de dois cliques para abrir.' },
          { title: 'Nomes longos com códigos quebravam após login.' },
          { title: 'Com Flgpwn, digitação desalinhada; corrigido.' },
          { title: 'Permissões do Gcontrol não refletiam botões em PC; corrigido.' },
          { title: 'Certa sintaxe na mensagem comportava-se como sticker; corrigido.' },
          { title: 'Colar texto enorme travava o navegador; corrigido.' },
          { title: 'Gestor de powers de grupo às vezes não listava powers; corrigido.' },
          { title: 'Chats incorporados fora do xat mostravam como não registrado por mudanças de privacidade; mitigado com fluxo de consentimento futuro.' },
          { title: 'Cliques em anúncios às vezes abriam grupo errado (web).' },
        ],
      },
      {
        type: 'new',
        label: 'Web',
        count: 1,
        items: [
          {
            title: 'Desatribuir todos os powers de grupo',
            details: ['Possível na página de login (web).'],
          },
        ],
      },
    ],
  },
];

export const changelogXat: ChangelogEntry[] = [...changelogCore, ...changelogLegacy];

/** @deprecated Prefer `changelogXat` — mantido para o painel e imports existentes. */
export const changelog: ChangelogEntry[] = changelogXat;
